import { type ReactNode, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useAuth } from "./AuthContext";
import type { KcRole } from "./keycloak";

interface PrivateRouteProps {
  children: ReactNode;
  requiredRole?: KcRole;
}

/**
 * Route guard. If the user is not authenticated, kicks off the configured
 * sign-in flow (preserving the current path as redirect target). If authenticated but
 * missing the required role, redirects to `/unauthorized`.
 *
 * <p>Also shows a waiting spinner while {@code promotingToDoctor} is true,
 * because the JWT has the old role until the post-signup token refresh
 * completes.
 */
export function PrivateRoute({ children, requiredRole }: PrivateRouteProps) {
  const { initialized, isAuthenticated, roles, login, promotingToDoctor } =
    useAuth();
  const location = useLocation();

  useEffect(() => {
    if (initialized && !isAuthenticated) {
      login(location.pathname + location.search);
    }
  }, [initialized, isAuthenticated, login, location]);

  if (!initialized || !isAuthenticated || promotingToDoctor) {
    return (
      <Box
        sx={{
          display: "grid",
          placeItems: "center",
          minHeight: "60vh",
          gap: 2,
        }}
      >
        <CircularProgress />
        {promotingToDoctor && (
          <Typography color="text.secondary">
            Setting up your doctor account…
          </Typography>
        )}
      </Box>
    );
  }

  if (requiredRole && !roles.includes(requiredRole)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}
